import { z } from 'zod'

import { userInvitationSchema } from './permission'

/** Handle format: letters, numbers, underscore; 1–50 chars. Normalizes to lowercase. */
export const tenantHandleSchema = z.string()
  .min(1, 'handle required')
  .max(50, 'handle too long')
  .regex(/^\w+$/, 'handle must be letters, numbers, and underscores only')
  .transform(s => s.toLowerCase())

export enum DateFormat {
  MM_DD_YYYY = 'MM_DD_YYYY',
  DD_MM_YYYY = 'DD_MM_YYYY',
  YYYY_MM_DD = 'YYYY_MM_DD'
}

/** Tenant feature flags (persisted in global tenant table). Defaults applied when missing. */
export const tenantFeatureFlagsSchema = z.object({
  /**
   * Proof of Play: record and report content displayed on screens (billing / advertiser trust).
   * Stripe-owned for self-serve (per-player add-on). Effective runtime also follows Book via
   * `isProofOfPlayFeatureEnabled` (Book OR). Default false after Phase B — new orgs buy seats
   * (or get Book); existing rows were backfilled to true and keep their stored value.
   */
  proofOfPlay: z.boolean(),
  /**
   * Super-admin pilot: record proof-of-play on players and run rollups without commercial
   * entitlement (`proofOfPlay` / Book). Reports API is super-admin only; Studio PoP UI only
   * during impersonation. Remove after pilot tenants move to the Stripe add-on.
   */
  proofOfPlayPilotIngest: z.boolean(),
  /** Widget file upload: allow uploading widget bundles (.tgz) via POST /assets. When false, widget bundle uploads return 403. Default false. */
  widgetUpload: z.boolean(),
  /** Widget builder: allow creating/editing widgets from builder (POST/PUT .../assets/builder, GET .../source). When false, builder routes return 403. Default false. */
  widgetBuilder: z.boolean(),
  /** URL widget: allow creating URL widgets via POST .../assets/url-widget. When false, url-widget route returns 403. Default false. */
  widgetUrl: z.boolean(),
  /** Playlist export/import: allow exporting playlists as .tgz (GET .../playlists/:b64Urn/export) and importing .tgz (POST .../playlists/import). When false, those routes return 403 and the app hides Export/Import. Default false; super-admins toggle in Companies → Feature flags. */
  playlistExportImport: z.boolean(),
  /**
   * Audience analytics (aggregates from on-device CV over webcam/USB input): tenant may opt in per player; no raw video upload.
   * When false, audience-related player settings and heartbeat payloads are ignored. Default false.
   */
  audienceAnalytics: z.boolean(),
  /**
   * Operator live preview of the audience camera stream in the management app (WebRTC). Independent of aggregate analytics.
   * When false, preview-related APIs/UI are disabled. Default false.
   */
  audienceWebcamPreview: z.boolean(),
  /**
   * Person-class COCO-SSD aggregates (qualified tracks + dwell) on top of motion sampling. Super-admin only; requires `audienceAnalytics`.
   * When false, CV fields on heartbeat aggregates are stripped server-side. Default false.
   */
  audiencePersonCvAnalytics: z.boolean(),
  /**
   * Getting-started one-click demo import / playlist / assign. When false, tenant onboarding demo API routes return 403
   * and the management app hides quick-setup actions. Default true.
   */
  onboardingDemoQuickSetup: z.boolean(),
  /**
   * Lucimark Book: portal bookings, Stripe Connect payouts, ad-order review, and bookable screens.
   * When false, marketplace operator routes return 403 and Book UI is hidden (upsell via SaaS plan). Default false.
   */
  marketplaceBook: z.boolean(),
  /**
   * Lucimark Elo: curated human-creator creative exchange (discovery, authored offers).
   * When false, Elo API routes return 403 and Studio Elo nav is hidden. Default false (pilot opt-in).
   */
  lucimarkElo: z.boolean(),
  /**
   * Android hybrid native playback surface (Media3 under WebView for progressive MP4).
   * Product default **on**. When false, super-admin kill-switch — Android keeps WebView `<video>`.
   * Requires native shell ≥ 0.0.13; Web OTA alone cannot enable Media3.
   */
  nativePlaybackSurface: z.boolean(),
  /**
   * Simplified Telas (players) inventory UI: status filters + split detail panel without folders/tags
   * chrome or dense tabs (#2567). When false, Studio keeps the full inventory. Default false (opt-in).
   */
  simplifiedTelasUi: z.boolean()
})

export type TTenantFeatureFlags = z.infer<typeof tenantFeatureFlagsSchema>

/**
 * Subset of `featureFlags` that organization admins (non–super-admin) may send on
 * `PUT /tenants/:id` with `tenant_settings.update`. Super-admins may PATCH any key
 * using `tenantRecordUpdateParamsSchema.featureFlags` (`tenantFeatureFlagsSchema.partial()`).
 */
export const tenantAdminEditableFeatureFlagsPatchSchema = z.object({
  /** Tenant-wide opt-in for audience analytics; when false, players cannot use analytics for this org. */
  audienceAnalytics: z.boolean().optional()
}).strict()

export type TTenantAdminEditableFeatureFlagsPatch = z.infer<typeof tenantAdminEditableFeatureFlagsPatchSchema>

export const DEFAULT_TENANT_FEATURE_FLAGS: TTenantFeatureFlags = {
  /** Phase B: new tenants start unpaid until Stripe add-on / Book (existing rows keep backfilled true). */
  proofOfPlay: false,
  proofOfPlayPilotIngest: false,
  widgetUpload: false,
  widgetBuilder: false,
  widgetUrl: false,
  playlistExportImport: false,
  audienceAnalytics: false,
  audienceWebcamPreview: false,
  audiencePersonCvAnalytics: false,
  onboardingDemoQuickSetup: true,
  marketplaceBook: false,
  lucimarkElo: false,
  nativePlaybackSurface: true,
  simplifiedTelasUi: false
}

/** Web OTA apply policy for registered players (`immediate` vs pre-download and apply after next app restart). Stored in tenant.settings JSON. */
export const webOtaApplyTimingSchema = z.enum(['immediate', 'next_app_restart'])
export type TWebOtaApplyTiming = z.infer<typeof webOtaApplyTimingSchema>

/** Field-service kiosk PIN shape: 4–6 digits, short enough to read out over the phone. */
export const FIELD_SERVICE_PIN_PATTERN = /^\d{4,6}$/

/** Normalizes a raw field-service PIN; anything that is not 4–6 digits resolves to '' (unset, ungated). */
export function resolvePlayerFieldServicePin(raw: unknown): string {
  if (typeof raw !== 'string') { return '' }
  const trimmed = raw.trim()
  return FIELD_SERVICE_PIN_PATTERN.test(trimmed) ? trimmed : ''
}

export const tenantSettingsSchema = z.object({
  language: z.string(),
  dateFormat: z.nativeEnum(DateFormat),
  /**
   * Web OTA apply policy for registered players. `immediate` = download and apply as soon as possible.
   * `next_app_restart` = fetch/cache updates as soon as offered, apply (reload) after next app restart. Default `immediate` when missing.
   */
  playerWebOtaApplyTiming: webOtaApplyTimingSchema.optional(),
  /**
   * When true, registered players hide operator toasts (campaign updated, group sync change) on screen.
   * New tenant rows default true (see global-schema `tenants.settings` defaultFn). When omitted on legacy JSON, resolved as false (toasts on).
   */
  playerOperatorNotificationsMuted: z.boolean().optional(),
  /**
   * When false, registered Android players are not offered APK updates on WebSocket handshake; operators trigger updates from the Players list. Default true when missing.
   */
  playerAndroidApkAutoUpdate: z.boolean().optional(),
  /**
   * When true, registered players only enqueue frames whose `layout.aspectRatio` matches the device viewport
   * (window inner width × height). Default true when missing; set false to opt out.
   */
  playerRestrictPlaybackToDeviceAspectRatio: z.boolean().optional(),
  /**
   * When true, players dissolve between creatives including warm/predecoded stills.
   * Default true when missing; set false to hard-cut. Applies after next registration-status refresh.
   */
  playerCreativeCrossfade: z.boolean().optional(),
  /**
   * Dissolve duration in ms when `playerCreativeCrossfade` is on.
   * Optional; resolved default {@link DEFAULT_PLAYER_CREATIVE_CROSSFADE_MS}, clamped
   * {@link PLAYER_CREATIVE_CROSSFADE_MS_MIN}–{@link PLAYER_CREATIVE_CROSSFADE_MS_MAX}.
   */
  playerCreativeCrossfadeMs: z.number().int().optional(),
  /**
   * When true, low-capacity devices may use creative crossfade (follows `playerCreativeCrossfade`).
   * New tenant rows default false (see {@link DEFAULT_NEW_TENANT_SETTINGS}). When omitted on legacy JSON, resolved as false (hard-cut on low).
   */
  playerCreativeCrossfadeOnLowCapacity: z.boolean().optional(),
  /**
   * When true, low-capacity devices play video/HLS like mid/high.
   * New tenant rows default true (see {@link DEFAULT_NEW_TENANT_SETTINGS}). When omitted on legacy JSON, resolved as false (skip video frames on low).
   */
  playerAllowVideoOnLowCapacity: z.boolean().optional(),
  /**
   * Digits a field technician types on an Android kiosk screen to leave Lock Task (open Android Settings).
   * Empty string clears it; when unset, kiosk unlock is not gated so nobody is locked out of an
   * unconfigured screen. Operational lock against casual tampering only — it is stored as written,
   * displayed in Studio by design, and readable by every player of the tenant. Not a credential.
   */
  playerFieldServicePin: z.union([
    z.literal(''),
    z.string().regex(FIELD_SERVICE_PIN_PATTERN, 'PIN must be 4 to 6 digits')
  ]).optional(),
  /**
   * When true, Android screens provisioned as Device Owner arm hard kiosk (Lock Task) and gate
   * Settings behind the field-service PIN. When false, screens leave (and stay out of) Lock Task —
   * Studio's own off switch, independent of Device Owner provisioning. Default true when missing.
   */
  playerAndroidKioskLockEnabled: z.boolean().optional()
})

/** Default dissolve length when crossfade is on and the setting is omitted. */
export const DEFAULT_PLAYER_CREATIVE_CROSSFADE_MS = 300
/** Inclusive minimum tenant-tunable dissolve length (ms). */
export const PLAYER_CREATIVE_CROSSFADE_MS_MIN = 150
/** Inclusive maximum tenant-tunable dissolve length (ms). */
export const PLAYER_CREATIVE_CROSSFADE_MS_MAX = 800

/** Clamp / default tenant creative crossfade duration (ms). */
export function resolvePlayerCreativeCrossfadeMs(raw: unknown): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) {
    return DEFAULT_PLAYER_CREATIVE_CROSSFADE_MS
  }
  const n = Math.round(raw)
  if (n < PLAYER_CREATIVE_CROSSFADE_MS_MIN) {
    return PLAYER_CREATIVE_CROSSFADE_MS_MIN
  }
  if (n > PLAYER_CREATIVE_CROSSFADE_MS_MAX) {
    return PLAYER_CREATIVE_CROSSFADE_MS_MAX
  }
  return n
}

/** Resolved tenant settings exposed to the app (defaults applied). */
export const resolvedTenantSettingsSchema = z.object({
  playerWebOtaApplyTiming: webOtaApplyTimingSchema,
  playerOperatorNotificationsMuted: z.boolean(),
  playerAndroidApkAutoUpdate: z.boolean(),
  playerRestrictPlaybackToDeviceAspectRatio: z.boolean(),
  playerCreativeCrossfade: z.boolean(),
  playerCreativeCrossfadeMs: z.number().int(),
  playerCreativeCrossfadeOnLowCapacity: z.boolean(),
  playerAllowVideoOnLowCapacity: z.boolean(),
  /** '' when unset — kiosk unlock is then ungated. */
  playerFieldServicePin: z.string(),
  /** Studio's Android kiosk lock switch; default true when missing. */
  playerAndroidKioskLockEnabled: z.boolean()
})

export type TResolvedTenantSettings = z.infer<typeof resolvedTenantSettingsSchema>

export const DEFAULT_RESOLVED_TENANT_SETTINGS: TResolvedTenantSettings = {
  playerWebOtaApplyTiming: 'immediate',
  playerOperatorNotificationsMuted: false,
  playerAndroidApkAutoUpdate: true,
  playerRestrictPlaybackToDeviceAspectRatio: true,
  playerCreativeCrossfade: true,
  playerCreativeCrossfadeMs: DEFAULT_PLAYER_CREATIVE_CROSSFADE_MS,
  playerCreativeCrossfadeOnLowCapacity: false,
  playerAllowVideoOnLowCapacity: false,
  playerFieldServicePin: '',
  playerAndroidKioskLockEnabled: true
}

/**
 * Seeded into new `tenant.settings` rows (global-schema `$defaultFn` and create/provision paths).
 * Legacy rows that omit keys still resolve via {@link resolveTenantSettings} (low-capacity opts stay false when missing).
 */
export const DEFAULT_NEW_TENANT_SETTINGS: TTenantSettings = {
  language: 'ptBR',
  dateFormat: DateFormat.DD_MM_YYYY,
  /** New tenants: hide campaign/sync corner toasts on players until turned off in Settings. */
  playerOperatorNotificationsMuted: true,
  /** New tenants: only playlist frames matching the player viewport aspect ratio (opt out in Settings). */
  playerRestrictPlaybackToDeviceAspectRatio: true,
  /** New tenants: creative crossfade on (including warm stills); opt out in Settings. */
  playerCreativeCrossfade: true,
  /** New tenants: default dissolve length (ms); tunable in Settings when crossfade is on. */
  playerCreativeCrossfadeMs: DEFAULT_PLAYER_CREATIVE_CROSSFADE_MS,
  /** New tenants: low-capacity devices hard-cut (opt in via Settings). */
  playerCreativeCrossfadeOnLowCapacity: false,
  /** New tenants: low-capacity devices may play video (opt out in Settings). */
  playerAllowVideoOnLowCapacity: true
}

/** Normalizes raw tenant settings JSON into resolved settings (player-facing options only). */
export function resolveTenantSettings(raw: Partial<TTenantSettings> | null | undefined): TResolvedTenantSettings {
  if (!raw) { return { ...DEFAULT_RESOLVED_TENANT_SETTINGS } }
  const timing = raw.playerWebOtaApplyTiming
  const playerWebOtaApplyTiming: TWebOtaApplyTiming = timing === 'next_app_restart' ? 'next_app_restart' : 'immediate'
  return {
    playerWebOtaApplyTiming,
    playerOperatorNotificationsMuted: raw.playerOperatorNotificationsMuted === true,
    playerAndroidApkAutoUpdate: raw.playerAndroidApkAutoUpdate !== false,
    playerRestrictPlaybackToDeviceAspectRatio: raw.playerRestrictPlaybackToDeviceAspectRatio !== false,
    playerCreativeCrossfade: raw.playerCreativeCrossfade !== false,
    playerCreativeCrossfadeMs: resolvePlayerCreativeCrossfadeMs(raw.playerCreativeCrossfadeMs),
    playerCreativeCrossfadeOnLowCapacity: raw.playerCreativeCrossfadeOnLowCapacity === true,
    playerAllowVideoOnLowCapacity: raw.playerAllowVideoOnLowCapacity === true,
    playerFieldServicePin: resolvePlayerFieldServicePin(raw.playerFieldServicePin),
    playerAndroidKioskLockEnabled: raw.playerAndroidKioskLockEnabled !== false
  }
}

/**
 * Plan limits (allowed) and their excess. Billing is by screens only (decision 2026-09-30):
 * seats and storage are not plan dimensions — see {@link usedQuotasSchema} and `tenantStorageLimit.ts`.
 */
export const quotasSchema = z.object({
  players: z.number()
})

/**
 * Current usage. `users` and `storageMb` are usage metrics (analytics / engagement / cost); neither is
 * sold by a plan. `storageMb` is only guarded by the technical cap `tenantStorageTechnicalCapMb`.
 */
export const usedQuotasSchema = quotasSchema.extend({
  storageMb: z.number(),
  users: z.number()
})

export const tenantQuotasSchema = z.object({
  allowed: quotasSchema,
  used: usedQuotasSchema,
  excess: quotasSchema,
  exceeded: z.boolean(),
  /** Internal media storage cap derived from `allowed.players` (not a billed limit). */
  storageCapMb: z.number()
})

export const tenantRecordParamsSchema = z.object({
  name: z.string(),
  handle: tenantHandleSchema.optional(),
  website: z.string().optional(),
  description: z.string().optional(),
  logoUrn: z.string().optional(),
  settings: tenantSettingsSchema.optional(),
  allowedQuotas: quotasSchema,
  invitations: z.array(userInvitationSchema),
  /** Clerk `user_…` — Stripe Customer search/reuse via `lucimark_user_id` metadata (optional). */
  ownerUserId: z.string().optional()
})

export const tenantRecordUpdateParamsSchema = tenantRecordParamsSchema.partial().extend({
  logoUrn: z.string().nullish(),
  logoLightUrn: z.string().nullish(),
  playerLogoUrn: z.string().nullish(),
  playerBackgroundUrn: z.string().nullish(),
  featureFlags: tenantFeatureFlagsSchema.partial().optional()
})

export type TTenantSettings = z.infer<typeof tenantSettingsSchema>
export type TQuotas = z.infer<typeof quotasSchema>
export type TUsedQuotas = z.infer<typeof usedQuotasSchema>
export type TTenantQuotas = z.infer<typeof tenantQuotasSchema>
export type TTenantRecordParams = z.infer<typeof tenantRecordParamsSchema>
export type TTenantRecordUpdateParams = z.infer<typeof tenantRecordUpdateParamsSchema>
export type TGetTenantMetadata = () => { tenantId: string | null, quotas: TTenantQuotas }
